import { LocationTracker } from "@repo/helpers";
import { keyPair } from "@repo/models";
import { Context, createContext, ReactNode, useContext, useMemo, useRef } from "react";

import { CustomComponentProps } from "../dynamicComponent/types";
import { MappedComponent } from "../models/mappedComponent";
import { PendingEdits, PendingEditsContext } from "./pendingEdits";

/**
 * Holds all items needed specifically for a dynamic component
 * instead of just passing props thru
 * Intended for efficiency
 */
export interface ComponentContextType {
	valueChanged: (value: any, id: string, parentComponentName?: string, dontEdit?: boolean) => Promise<void>;
	valuesChanged?: (values: keyPair[], parentComponentName?: string, dontEdit?: boolean) => Promise<void>;
	setCopiedFrom?: (component: MappedComponent) => Promise<void>;

	//Function specific to file components
	deleteUploadedFile?: (id: number, propName: string) => Promise<void>;
	getUploadedFile?: (id: number) => void;

	/** Fn to return custom components (not defined in DynamicComponent switch) */
	getCustomComponent?: (props: CustomComponentProps) => JSX.Element;
	getComponentAddon?: (propertyName: string) => JSX.Element;
	getComponentAddonPosition?: (propertyName: string) => "top" | "bottom" | "left" | "right";
	customFunctions?: (name: string, other?: any) => (params?: any) => Promise<any>;
	locationTracker?: LocationTracker;
}

const defaultContextValues = {
	valueChanged: undefined,
	valuesChanged: undefined,
	setCopiedFrom: undefined,
	deleteUploadedFile: undefined,
	getUploadedFile: undefined,
	getCustomComponent: undefined,
	getComponentAddon: undefined,
	getComponentAddonPosition: undefined,
	customFunctions: undefined,
	locationTracker: undefined,
} as ComponentContextType;

interface ComponentProps {
	children: ReactNode;
	valueChanged: (value: any, id: string, parentComponentName?: string, dontEdit?: boolean) => Promise<void>;
	valuesChanged?: (values: keyPair[], parentComponentName?: string, dontEdit?: boolean) => Promise<void>;
	setCopiedFrom?: (component: MappedComponent) => Promise<void>;
	deleteUploadedFile?: (id: number, propName: string) => Promise<void>;
	getUploadedFile?: (id: number) => void;
	getCustomComponent?: (props: CustomComponentProps) => JSX.Element;
	getComponentAddon?: (propertyName: string) => JSX.Element;
	getComponentAddonPosition?: (propertyName: string) => "top" | "bottom" | "left" | "right";
	customFunctions?: (name: string, other?: any) => (params?: any) => Promise<any>;
	locationTracker?: LocationTracker;
	/** Registry of field edits still waiting on DynamicComponent's debounce; a nested provider without one keeps the outer one */
	pendingEdits?: PendingEdits;
}

export const ComponentContext = createContext<ComponentContextType>(defaultContextValues);
export const useComponentContext = () => useContext(ComponentContext);

/** Callbacks DynamicComponent only invokes (never renders with), so they can be forwarded. */
const FORWARDED_CALLBACKS = [
	"valueChanged",
	"valuesChanged",
	"setCopiedFrom",
	"deleteUploadedFile",
	"getUploadedFile",
	"customFunctions",
] as const;
type ForwardedCallback = (typeof FORWARDED_CALLBACKS)[number];

/**
 * What DynamicComponent reads instead of the public context. Consumers usually pass new callback
 * functions on every render; if DynamicComponent read them directly, every field would re-render
 * whenever the consumer re-renders. Here each callback is a stable forwarder that calls the latest
 * function passed to ComponentProvider, so fields only re-render when their own data changes.
 * Custom components, component addons and anything using useComponentContext still see (and
 * re-render with) the public context exactly as before.
 */
export interface StableComponentContextType extends Pick<ComponentContextType, ForwardedCallback | "locationTracker"> {
	/** True when getComponentAddon is provided (the addon itself is rendered from the public context) */
	hasComponentAddon: boolean;
}

const StableComponentContext = createContext<StableComponentContextType>({
	...defaultContextValues,
	hasComponentAddon: false,
});
export const useStableComponentContext = () => useContext(StableComponentContext);

const InertContext = createContext<unknown>(null);

/**
 * While `subscribe` is true, re-renders the caller on every ComponentProvider render (as when it
 * read the public context), for components that render from data a consumer may mutate in place.
 */
export function useRerenderWithConsumer(subscribe: boolean) {
	useContext<unknown>(subscribe ? (ComponentContext as Context<unknown>) : InertContext);
}

export function ComponentProvider(props: ComponentProps) {
	const {
		children,
		valueChanged,
		valuesChanged,
		setCopiedFrom,
		deleteUploadedFile,
		getUploadedFile,
		getCustomComponent,
		getComponentAddon,
		getComponentAddonPosition,
		customFunctions,
		locationTracker,
	} = props;

	// Last function received for each forwarded callback. Kept even if a later render passes
	// null/undefined, so code still holding a forwarder (e.g. an async continuation) reaches a
	// function, like the directly captured callback it replaces would have. (DynamicComponent's own
	// debounced edit re-reads valueChanged when it re-renders, so it fails once valueChanged is
	// removed, exactly as before.)
	const latest = useRef<Partial<Record<ForwardedCallback, (...args: any[]) => any>>>({});
	for (const key of FORWARDED_CALLBACKS) {
		if (typeof props[key] === "function") latest.current[key] = props[key];
	}

	const forwarded = useForwarders(props, latest);
	const hasComponentAddon = !!getComponentAddon;
	const stableValue = useMemo<StableComponentContextType>(
		() => ({
			valueChanged: forwarded.valueChanged,
			valuesChanged: forwarded.valuesChanged,
			setCopiedFrom: forwarded.setCopiedFrom,
			deleteUploadedFile: forwarded.deleteUploadedFile,
			getUploadedFile: forwarded.getUploadedFile,
			customFunctions: forwarded.customFunctions,
			locationTracker,
			hasComponentAddon,
		}),
		[
			forwarded.valueChanged,
			forwarded.valuesChanged,
			forwarded.setCopiedFrom,
			forwarded.deleteUploadedFile,
			forwarded.getUploadedFile,
			forwarded.customFunctions,
			locationTracker,
			hasComponentAddon,
		],
	);

	const outerPendingEdits = useContext(PendingEditsContext);
	const pendingEdits = props.pendingEdits ?? outerPendingEdits;

	return (
		<ComponentContext.Provider
			value={{
				valueChanged,
				valuesChanged,
				setCopiedFrom,
				deleteUploadedFile,
				getUploadedFile,
				getCustomComponent,
				getComponentAddon,
				getComponentAddonPosition,
				customFunctions,
				locationTracker,
			}}
		>
			<StableComponentContext.Provider value={stableValue}>
				<PendingEditsContext.Provider value={pendingEdits}>{children}</PendingEditsContext.Provider>
			</StableComponentContext.Provider>
		</ComponentContext.Provider>
	);
}

/**
 * One forwarder per callback. A forwarder only exists while the prop is a function; any other value
 * (undefined, null) is passed through unchanged, since components branch on it being set.
 */
function useForwarders(
	props: ComponentProps,
	latest: { current: Partial<Record<ForwardedCallback, (...args: any[]) => any>> },
) {
	const forwarders = useRef<Partial<Record<ForwardedCallback, (...args: any[]) => any>>>({});
	const result = {} as Record<ForwardedCallback, any>;
	for (const key of FORWARDED_CALLBACKS) {
		const value = props[key];
		if (typeof value !== "function") {
			result[key] = value;
			continue;
		}
		forwarders.current[key] ??= (...args: any[]) => latest.current[key](...args);
		result[key] = forwarders.current[key];
	}
	return result;
}
